import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Expander from '@amzn/meridian/expander';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import { useTranslation } from 'src/arb-wrapper';
import {
  DA_GENERATED_PRODUCT_SET_PUBLISHERS,
  FEATURE_FLAGS,
  GTI_REGEX,
  ID_TYPES,
  getSupportedLocalesForPublisher,
} from 'src/constants';
import { isFeatureEnabled } from 'src/feature-flags';
import { useDebounce } from 'src/hooks/useDebounce';
import { useProductSetValidation } from 'src/hooks/useProductSetValidation';
import {
  type Locale,
  type CampaignTitlesControllerGetCampaignTitlesApiArg,
  type CampaignTitlesControllerGetCampaignTitlesApiResponse,
  type CampaignDesiredStateDto,
  type Country,
  type BusinessLine,
} from 'src/store/generated/leela-api-generated';
import { useCampaignTitlesControllerGetCampaignTitlesQuery } from 'src/store/leela-api';
import { BusinessLines, SportsBusinessLines } from 'src/store/constants';
import { FormInput } from 'src/components/common/forms/FormInput';
import { FormLocalization } from 'src/components/campaigns/forms/FormLocalization';
import { FormSelect } from 'src/components/common/forms/FormSelect';
import { FormTemplatesSelect } from 'src/components/campaigns/forms/FormTemplatesSelect';
import { StrategyScheduleWindows } from 'src/components/campaigns/forms/StrategyScheduleWindows';
import { type FormRhfControllerProps } from 'src/components/common/forms/types';
import { FromSonataIdEntry } from 'src/components/campaigns/forms/FormSonataIdEntry';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { useGetBenderTemplatesQuery } from 'src/store/bender-api';
import Text from '@amzn/meridian/text';
import { FormAbTestingControls } from 'src/components/campaigns/forms/FormAbTestingControls';
import { FormAutoGenerateControls } from 'src/components/campaigns/forms/FormAutoGenerateControls';
import { ProductSetDisplay } from 'src/components/campaigns/ProductSetDisplay';

export const FormProductStrategyContext = createContext<{
  hideLatestTitlesData?: boolean;
  idType?: string;
  isDebouncing?: boolean;
  isError?: boolean;
  isFetching?: boolean;
  isLoading?: boolean;
  pageId?: string;
  sonataIdErrorMsg?: string;
  strategyId?: string;
  gtiList?: string[];
  titles?: CampaignTitlesControllerGetCampaignTitlesApiResponse;
  setIdType?: (value: React.SetStateAction<string>) => void;
  onGtiValidateModeChange?: (isValidateMode: boolean) => void;
  gtiTitleMap?: Map<string, { title?: string; coverImageUrl?: string }>;
}>({});

const CAMPAIGN_TITLES_DEBOUNCE_DELAY = 500;

/** Count valid GTIs from the raw form value (string or array), so the name shows a real count. */
const countValidGtis = (gtiList: unknown): number => {
  if (typeof gtiList === 'string') {
    return gtiList.split(/[\n,]/).map(g => g.trim()).filter(g => g.length > 0 && GTI_REGEX.test(g)).length;
  }
  if (Array.isArray(gtiList)) {
    return gtiList.filter((g): g is string => typeof g === 'string' && GTI_REGEX.test(g)).length;
  }
  return 0;
};

interface FormProductStrategyProps extends FormRhfControllerProps {
  index: number;
  publisher: CampaignDesiredStateDto['publisher'];
  onDelete: () => void;
  canRemove?: boolean;
  isEditing?: boolean;
  // True when the strategy already exists on the saved campaign (has a productStrategyId).
  isPersisted?: boolean;
  /** Whether this card is the only rendered strategy, in which case it starts expanded. */
  isOnlyStrategy?: boolean;
  isAbTestingGroup?: boolean;
  variationCount?: number;
  productSets?: Array<{ productSetId: string; productSetName?: string }>;
  allowedCountries?: string[];
  allowedBusinessLines?: string[];
  campaignCountry?: string;
  campaignBusinessLine?: string;
  /** Countries offered by the publisher's account configs; used when no config narrows the list. */
  publisherCountries: readonly Country[];
  /** Countries the user's MANAGE_CAMPAIGNS scope allows; omitted means unrestricted. */
  scopeCountries?: readonly string[];
  publisherTimezone: string;
}

export const FormProductStrategy: React.FC<FormProductStrategyProps> = ({
  control,
  index,
  name,
  publisher,
  onDelete,
  canRemove = false,
  isEditing = false,
  isPersisted = false,
  isOnlyStrategy = false,
  isAbTestingGroup = false,
  variationCount = 0,
  productSets = [],
  allowedCountries,
  allowedBusinessLines,
  campaignCountry,
  campaignBusinessLine,
  publisherCountries,
  scopeCountries,
  publisherTimezone,
}) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const { validateProductSetDebounced, validationState, isDebouncing: isProductSetDebouncing } = useProductSetValidation();

  const [hideLatestTitlesData, setHideLatestTitlesData] = useState(false);
  const [idType, setIdType] = useState(ID_TYPES.STRATEGY_ID);
  const indexedName = useMemo(() => `${name}.${index}`, [name, index]);
  const pageIdFieldName = useMemo(
    () => `${indexedName}.${ID_TYPES.PAGE_ID}`,
    [indexedName],
  );
  const strategyIdFieldName = useMemo(
    () => `${indexedName}.${ID_TYPES.STRATEGY_ID}`,
    [indexedName],
  );
  const gtiListFieldName = useMemo(
    () => `${indexedName}.${ID_TYPES.GTI_LIST}`,
    [indexedName],
  );

  // The selected account config's countries win; an unscoped or absent config falls back to
  // everything the publisher's configs can serve. Either way the user's scope narrows the list.
  const supportedCountries = useMemo(() => {
    const configCountries = allowedCountries?.length ? allowedCountries as Country[] : [...publisherCountries];
    return scopeCountries ? configCountries.filter(c => scopeCountries.includes(c)) : configCountries;
  }, [allowedCountries, publisherCountries, scopeCountries]);

  // Get publisher-specific supported locales
  const supportedLocales = useMemo(() => {
    return getSupportedLocalesForPublisher(publisher);
  }, [publisher]);

  const { getValues, trigger, unregister, setValue } = useFormContext();

  // Auto-set country and businessLine from campaign-level values (create only)

  const selectedLocales: Locale[] = useWatch({
    control,
    name: `${indexedName}.locales`,
  });

  const productSetId = useWatch({
    control,
    name: `${indexedName}.productSetId`,
  });

  const productSetName = useWatch({
    control,
    name: `${indexedName}.productSetName`,
  });

  const autoGenerateProductSet = useWatch({
    control,
    name: `${indexedName}.autoGenerateProductSet`,
  }) ?? (publisher === 'adsp');

  const countryCode = useWatch({ control, name: `${indexedName}.country` });
  const locales = useWatch({ control, name: `${indexedName}.locales` });
  const pageId = useWatch({ control, name: pageIdFieldName });
  const strategyId = useWatch({ control, name: strategyIdFieldName });
  const gtiList = useWatch({ control, name: gtiListFieldName });
  const businessLine = useWatch({ control, name: `${indexedName}.businessLine` });
  const strategyName = useWatch({ control, name: `${indexedName}.name` });

  useEffect(() => {
    if (isEditing) {
      return;
    }

    if (campaignCountry) {
      setValue(`${indexedName}.country`, campaignCountry);
    }

    if (campaignBusinessLine) {
      setValue(`${indexedName}.businessLine`, campaignBusinessLine);
    };
  }, [campaignCountry, campaignBusinessLine, indexedName, setValue, isEditing]);

  // Default name: "Strategy N: {locale} · {source}" where N is the strategy's 1-based position,
  // locale is the first selected locale, and source = pageId / strategyId / "GTIs (count)".
  const localeLabel = Array.isArray(locales) ? locales[0] ?? '' : '';
  const nameSource = [pageId, strategyId].find(Boolean) ??
    (countValidGtis(gtiList) ? `GTIs (${countValidGtis(gtiList)})` : '');
  const defaultName = `Strategy ${index + 1}: ${[localeLabel, nameSource].filter(Boolean).join(' · ')}`.trim();

  // Pre-fill the default name, but never overwrite a user edit (tracked via the ref). Skipped for
  // persisted strategies so a saved name is preserved.
  const lastAutoNameRef = useRef<string>('');
  useEffect(() => {
    if (isPersisted) {
      return;
    }
    const current = (strategyName ?? '').trim();
    if (current === '' || current === lastAutoNameRef.current) {
      lastAutoNameRef.current = defaultName;
      setValue(`${indexedName}.name`, defaultName);
    }
  }, [defaultName, strategyName, indexedName, setValue, isPersisted]);

  // A/B Testing state - watch form fields
  const abTestingEnabled = useWatch({ control, name: `${indexedName}.abTestingEnabled` }) ?? false;
  const abTestingVariations = useWatch({ control, name: `${indexedName}.abTestingVariations` }) ?? 2;

  // A/B testing is available for META/TIKTOK when feature flag is enabled and not in edit mode
  const showAbTesting = isFeatureEnabled(FEATURE_FLAGS.ENABLE_AB_TESTING) && DA_GENERATED_PRODUCT_SET_PUBLISHERS.includes(publisher) && publisher !== 'adsp' && !isEditing;

  // Parse and validate GTI list
  const validGtiList = useMemo(() => {
    if (!gtiList) return [];

    if (typeof gtiList === 'string') {
      return gtiList
        .split(/[\n,]/)
        .map(gti => gti.trim())
        .filter(gti => gti.length > 0 && GTI_REGEX.test(gti));
    } else if (Array.isArray(gtiList)) {
      return gtiList.filter((gti): gti is string => typeof gti === 'string' && GTI_REGEX.test(gti));
    }

    return [];
  }, [gtiList]);

  // Track GTI validate mode to prevent API calls while typing
  const [isGtiValidateMode, setIsGtiValidateMode] = useState(false);

  // Track previous GTI list to detect deletions vs additions
  const prevValidGtiListRef = useRef<string[]>([]);

  const { data: supportedTemplates, error: benderTemplatesFetchingError } =
    useGetBenderTemplatesQuery({
      filters: {
        publishers: [publisher],
        supportedLocales: selectedLocales,
        businessLine: !isEditing && businessLine ? [businessLine] : undefined,
      },
    });

  useEffect(() => {
    if (benderTemplatesFetchingError) {
      createToast({
        type: AlertType.ERROR,
        message: t('form_bender_fetch_templates_error'),
      });
    }
  }, [benderTemplatesFetchingError, createToast, t]);

  const [campaignTitlesQueryArgs, setCampaignTitlesQueryArgs] = useState<
  CampaignTitlesControllerGetCampaignTitlesApiArg | undefined
  >(undefined);
  const [debouncedCampaignTitlesQueryArgs, isDebouncing] = useDebounce(
    campaignTitlesQueryArgs,
    CAMPAIGN_TITLES_DEBOUNCE_DELAY,
  );

  const {
    currentData: titles,
    error,
    isError,
    isFetching,
    isLoading,
    isSuccess,
  } = useCampaignTitlesControllerGetCampaignTitlesQuery(
    debouncedCampaignTitlesQueryArgs as CampaignTitlesControllerGetCampaignTitlesApiArg,
    { skip: debouncedCampaignTitlesQueryArgs === undefined },
  );

  const sonataIdErrorMsg = useMemo(() => {
    if (error) {
      if (!countryCode) {
        return t('form_country_error');
      } else {
        return idType === ID_TYPES.PAGE_ID
          ? t('form_sonata_page_id_error')
          : t('form_sonata_strategy_id_error');
      }
    } else {
      return undefined;
    }
  }, [countryCode, error, idType, t]);

  useEffect(() => {
    // Trigger validation on change to idType, isError, and isSuccess, to enable save if field is valid without requiring subsequent blur
    if (!isFetching && !!getValues(strategyIdFieldName)) {
      trigger(strategyIdFieldName);
    }
    if (!isFetching && !!getValues(pageIdFieldName)) {
      trigger(pageIdFieldName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idType, isError, isFetching, isSuccess]);

  useEffect(() => {
    // Auto-select the appropriate ID type based on existing form data
    // Priority: GTI_LIST > PAGE_ID > STRATEGY_ID (most specific to least specific)
    // Only auto-select on initial load when no manual selection has been made
    if (validGtiList.length > 0) {
      setIdType(ID_TYPES.GTI_LIST);
    } else if (pageId && !strategyId) {
      setIdType(ID_TYPES.PAGE_ID);
    } else if (strategyId && !pageId) {
      setIdType(ID_TYPES.STRATEGY_ID);
    }
    // Only run when the actual form values change, not on every render
  }, [validGtiList.length, pageId, strategyId]);

  // Clean up form fields when switching between ID types
  useEffect(() => {
    if (pageId && idType === ID_TYPES.PAGE_ID) {
      unregister(strategyIdFieldName);
      unregister(gtiListFieldName);
    }
  }, [pageId, idType, strategyIdFieldName, gtiListFieldName, unregister]);

  useEffect(() => {
    if (strategyId && idType === ID_TYPES.STRATEGY_ID) {
      unregister(pageIdFieldName);
      unregister(gtiListFieldName);
    }
  }, [strategyId, idType, pageIdFieldName, gtiListFieldName, unregister]);

  useEffect(() => {
    if (validGtiList.length > 0 && idType === ID_TYPES.GTI_LIST) {
      unregister(pageIdFieldName);
      unregister(strategyIdFieldName);
    }
  }, [validGtiList.length, idType, pageIdFieldName, strategyIdFieldName, unregister]);

  useEffect(() => {
    // Clear title gallery when id type selection changes
    if (
      (idType === ID_TYPES.STRATEGY_ID && (pageId || gtiList)) ||
      (idType === ID_TYPES.PAGE_ID && (strategyId || gtiList)) ||
      (idType === ID_TYPES.GTI_LIST && (pageId || strategyId))
    ) {
      setHideLatestTitlesData(true);
    } else {
      setHideLatestTitlesData(false);
    }
  }, [idType, pageId, strategyId, gtiList]);

  useEffect(() => {
    if (isError || isSuccess) {
      setHideLatestTitlesData(false);
    }
  }, [isError, isSuccess]);

  useEffect(() => {
    if (!countryCode || !locales?.[0]) {
      setCampaignTitlesQueryArgs(undefined);
      return;
    }

    // For pageId/strategyId, make API call immediately
    if (pageId || strategyId) {
      setCampaignTitlesQueryArgs({
        countryCode,
        locale: locales[0],
        businessLine,
        pageId,
        strategyId,
      });
    } else if (validGtiList.length > 0 && isGtiValidateMode) {
      // Check if new list is a subset of previous (only deletions)
      const isSubset = validGtiList.every(gti => prevValidGtiListRef.current.includes(gti));
      const isOnlyDeletions = isSubset && validGtiList.length < prevValidGtiListRef.current.length;

      // Skip API call if only deletions (we already have title data)
      if (isOnlyDeletions) {
        return;
      }

      // Update ref only when making API call
      prevValidGtiListRef.current = validGtiList;

      // For GTI List, only make API call when in validate mode
      setCampaignTitlesQueryArgs({
        countryCode,
        locale: locales[0],
        businessLine,
        gtiList: validGtiList,
      });
    } else {
      // Clear query when not in validate mode or no valid data
      setCampaignTitlesQueryArgs(undefined);
      prevValidGtiListRef.current = [];
    }
  }, [countryCode, locales, businessLine, pageId, strategyId, validGtiList, isGtiValidateMode]);

  // ProductSet validation effect
  useEffect(() => {
    // Always validate if product set ID is provided and auto-generation is disabled
    if (productSetId && publisher && countryCode && !autoGenerateProductSet) {
      validateProductSetDebounced({
        publisher,
        productSetId,
        country: countryCode as Country,
        businessLine,
      });
    } else {
      validateProductSetDebounced(null);
    }
  }, [
    productSetId,
    publisher,
    countryCode,
    businessLine,
    autoGenerateProductSet,
    validateProductSetDebounced,
  ]);

  // Trigger form validation when product set validation state changes
  useEffect(() => {
    if (productSetId && !validationState.isLoading && !autoGenerateProductSet) {
      trigger(`${indexedName}.productSetId`);
    }
  }, [
    validationState.isLoading,
    productSetId,
    autoGenerateProductSet,
    trigger,
    indexedName,
  ]);

  // New and single-card strategies start expanded; persisted cards collapse only in multi-card campaigns.
  const [expanded, setExpanded] = useState(!isPersisted || isOnlyStrategy);

  // Edit-mode hydration can remount this card while the strategy count is transitional.
  // Re-open once it settles to one card; later manual collapses do not change isOnlyStrategy.
  useEffect(() => {
    if (isOnlyStrategy) {
      setExpanded(true);
    }
  }, [isOnlyStrategy]);

  // Expander title: strategy name or fallback
  const expanderTitle = strategyName || defaultName || `Strategy ${index + 1}`;

  return (
    <Box backgroundColor="primary" spacingInset="400" type="outline">
      <Row alignmentVertical="top" widths={['fill', 'fit']}>
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
        <Expander
          alwaysRenderChildren
          data-testid={`product-strategy-expander-${index}`}
          open={expanded}
          title={expanderTitle}
          type="inline"
          onChange={setExpanded}
        >
          <Column spacing="200" spacingInset="200 none none none">
            {/* A/B Testing Product Set IDs */}
            {isAbTestingGroup && variationCount > 1 && (
              <Column spacing="300">
                <Row alignmentVertical="center" spacing="200">
                  <Text type="b200">
                    {t('ab_testing_group_header', { count: variationCount })}
                  </Text>
                </Row>
                <ProductSetDisplay productSets={productSets} />
              </Column>
            )}

            <FormInput
              control={control}
              data-testid={`product-strategy-name-${index}`}
              label={t('form_product_strategy_name')}
              name={`${indexedName}.name`}
              trim
            />

            <Row
              alignmentHorizontal="justify"
              alignmentVertical="top"
              widths={['fill']}
            >
              {!(isAbTestingGroup && variationCount > 1) && (
                <Column spacing="200">
                  {publisher === 'adsp' && (
                    <Column spacing="none">
                      <Row spacing="200">
                        <Text type="b200">{t('product_set_label_adsp')}</Text>
                        <Text color="secondary" type="b100">
                          {productSetId || t('product_set_availability')}
                        </Text>
                      </Row>
                      <Row spacing="200">
                        <Text type="b200">{t('product_set_name_label_adsp')}</Text>
                        <Text color="secondary" type="b100">
                          {productSetName || t('product_set_name_availability')}
                        </Text>
                      </Row>
                    </Column>
                  )}
                  {DA_GENERATED_PRODUCT_SET_PUBLISHERS.includes(publisher) && publisher !== 'adsp' && (
                    <FormAutoGenerateControls
                      control={control}
                      disabled={isEditing || abTestingEnabled}
                      index={index}
                      indexedName={indexedName}
                      isEnabled={autoGenerateProductSet}
                      isValidating={validationState.isLoading || isProductSetDebouncing}
                      publisher={publisher}
                      validationState={validationState}
                      onToggle={(enabled: boolean) => {
                        setValue(`${indexedName}.autoGenerateProductSet`, enabled);
                        if (enabled) {
                          setValue(`${indexedName}.productSetId`, null);
                        }
                      }}
                    />
                  )}
                </Column>
              )}
            </Row>

            {showAbTesting && (
              <FormAbTestingControls
                disabled={isEditing}
                isEnabled={abTestingEnabled}
                variationCount={abTestingVariations}
                onToggle={(enabled) => {
                  setValue(`${indexedName}.abTestingEnabled`, enabled);
                  if (enabled) {
                    setValue(`${indexedName}.productSetId`, null);
                    setValue(`${indexedName}.autoGenerateProductSet`, true);
                    setValue(`${indexedName}.abTestingVariations`, 2);
                  }
                }}
                onVariationChange={(count) => {
                  setValue(`${indexedName}.abTestingVariations`, count);
                }}
              />
            )}

            {!campaignBusinessLine && (
              <FormSelect
                control={control}
                data-testid={`product-strategy-business-line-${index}`}
                disabled={isEditing}
                label={t('form_business_line')}
                name={`${indexedName}.businessLine`}
                options={[
                  ...Object.values(BusinessLines),
                  ...(isFeatureEnabled(FEATURE_FLAGS.ENABLE_SPORTS_BUSINESS_LINES) ? Object.values(SportsBusinessLines) : []),
                ]
                  .filter((bl) => !allowedBusinessLines?.length || allowedBusinessLines.includes(bl))
                  .map((businessLine) => ({
                    label: businessLine,
                    value: businessLine,
                  }))}
              />
            )}

            <FormLocalization
              control={control}
              data-testid={`product-strategy-localization-${index}`}
              disableCountry={isEditing || !!campaignCountry}
              hideCountry={!!campaignCountry}
              index={index}
              name={indexedName}
              supportedCountries={supportedCountries}
              supportedLocales={supportedLocales}
            />

            <FormProductStrategyContext.Provider
              value={{
                hideLatestTitlesData,
                idType,
                isDebouncing,
                isError,
                isFetching: debouncedCampaignTitlesQueryArgs !== undefined && isFetching && (idType !== ID_TYPES.GTI_LIST || isGtiValidateMode),
                isLoading,
                pageId,
                sonataIdErrorMsg,
                strategyId,
                gtiList: validGtiList,
                titles,
                setIdType,
                onGtiValidateModeChange: setIsGtiValidateMode,
                gtiTitleMap: titles && idType === ID_TYPES.GTI_LIST && isGtiValidateMode
                  ? new Map(titles.map(title => [title.gti ?? '', { title: title.title ?? undefined, coverImageUrl: title.coverImageUrl ?? undefined }]))
                  : undefined,
              }}
            >
              <FromSonataIdEntry
                control={control}
                gtiListFieldName={gtiListFieldName}
                index={index}
                pageIdFieldName={pageIdFieldName}
                strategyIdFieldName={strategyIdFieldName}
              />
            </FormProductStrategyContext.Provider>

            {Object.values(SportsBusinessLines).includes(businessLine as BusinessLine) && (
              <StrategyScheduleWindows
                control={control}
                publisherTimezone={publisherTimezone}
                strategyName={indexedName}
              />
            )}

            <FormTemplatesSelect
              control={control}
              countryCode={countryCode}
              data-testid={`product-strategy-templates-${index}`}
              name={indexedName}
              selectedLocales={selectedLocales}
              templates={supportedTemplates ?? []}
            />
          </Column>
        </Expander>
        </div>

        <Button
          aria-label={t('form_global_delete')}
          data-testid={`delete-product-strategy-button-${index}`}
          disabled={!canRemove || isPersisted}
          size="small"
          type="icon"
          onClick={onDelete}
        >
          <Icon tokens={trashTokens} />
        </Button>
      </Row>
    </Box>
  );
};
